import { a3Copy } from "@/components/assignments/a3-copy";
import { ScaleView } from "./work/scale/Scale";
import type { GradingScale } from "./work/scale/types";
/**
 * Feedback thread (issue #71, ADR 0002) — one attempt's judgement history.
 *
 * Append-only and oldest first: a revised judgement is a new event, so the
 * thread is the record of what a learner was told and when. A learner reads
 * only released events (RLS filters them), while an instructor also sees the
 * ones still withheld, which is why `visible_at === null` is rendered rather
 * than skipped. Each event says its outcome and its release as plain words on
 * its own line (ADR 0035 decision 6: state words are meta text, never a chip).
 */
import { Fragment } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { useAssignmentDate, assignmentNumber } from "./assignment-workspace-context";
import type { Feedback } from "@/lib/api/assignments";

export function FeedbackThread({
  events,
  emptyLabel,
  provisionalResultHref,
  scale,
}: {
  events: Feedback[];
  emptyLabel: string;
  provisionalResultHref?: string;
  /** Only the exact attempt's original terms, never the current assignment. */
  scale?: GradingScale | null;
}) {
  const date = useAssignmentDate();
  if (events.length === 0) {
    return <p className="text-ui text-muted-foreground">{emptyLabel}</p>;
  }

  const resultHref = provisionalResultHref && events.some(
    (event) => event.outcome === "graded" && event.visible_at !== null,
  ) ? provisionalResultHref : null;
  // The release is said only while it withholds: released is the norm.
  const release = (event: Feedback) => event.visible_at === null
    ? a3Copy.withheld
    : Date.parse(event.visible_at) > Date.now() ? a3Copy.withheldUntil(date(event.visible_at)) : null;

  return (
    <div className="grid gap-3">
      <ol className="divide-y divide-rule">
        {events.map((event) => {
          const facts = [
            event.outcome === "complete" ? a3Copy.complete : event.outcome === "missing" ? a3Copy.missing : event.outcome === "graded" ? a3Copy.graded : a3Copy.returnedForRevision,
            event.score != null ? <span className="font-data tabular-nums text-foreground">{assignmentNumber(event.score)}{a3Copy.pts}</span> : null,
            release(event),
          ].filter((fact) => fact !== null);
          return (
            <li key={event.id} className="grid gap-2 py-3 first:pt-0 last:pb-0">
              <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-meta text-muted-foreground">
                <span>
                  {facts.map((fact, index) => <Fragment key={index}>{index > 0 && " · "}<span>{fact}</span></Fragment>)}
                </span>
                <time dateTime={event.created_at} className="ms-auto tabular-nums">
                  {date(event.created_at)}
                </time>
              </p>
              {event.body && (
                <p className="whitespace-pre-wrap break-words text-ui text-foreground">{event.body}</p>
              )}
              {event.selected_scale_row_id && (scale
                ? <ScaleView scale={scale} selected={event.selected_scale_row_id} />
                : <p role="alert">This result's original grading scale is unavailable.</p>)}
            </li>
          );
        })}
      </ol>
      {resultHref && (
        <div>
          <Button asChild size="sm" variant="outline">
            <Link to={resultHref}>{a3Copy.viewProvisionalResult}</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
